import {useEffect, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {CEREMONY_PATH, DASHBOARD_PATH, PASSWORD_RECOVERY_PATH, EXCHANGE_GROUP_MEMBER_PATH} from '@constants';
import {ACCOUNT_REGISTRATION_PATH} from '@constants/routes';
import {getPostAuthRedirectUrl} from '@utils/groupTargetUtils';
import {AppOutlinedButton} from '@components/Core/buttons/AppOutlinedButton/AppOutlinedButton';
import EmailForm from './EmailForm/EmailForm';
import PasswordForm from '../../shared/PasswordForm/PasswordForm';
import SsoForm from './SsoForm/SsoForm';
import SSOIdentityView from './SSOIdentityView/SSOIdentityView';
import TwoFactorForm from './TwoFactorForm/TwoFactorForm';
import Loading from '../../shared/Loading/Loading';
import Notifications from '../Notifications/Notifications';
import OrDivider from '../../shared/Divider/OrDivider';
import {
    authenticateWithTwoFactor,
    fetchSsoIdps,
    setFederatedIdentities,
    setEmailSsoIdpsFetchedFor,
    setIsSSORequired,
    selectAuthError,
    selectEmailSsoIdpsFetchedFor,
    selectFederatedIdentities,
    selectIsSSORequired,
    selectIsSsoIdpsFetching,
    selectIsTwoFactorRequired
} from '../userSlice';
import {selectRefererUrl} from '../../appSlice';
import Buttons from './Buttons/Buttons';
import useErrorNotification from '../../../hooks/useErrorNotification';
import './loginForms.less';

export default function LoginForms() {
    const intl = useIntl();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const authError = useSelector(selectAuthError);
    const [searchParams] = useSearchParams();
    const refererUrl = useSelector(selectRefererUrl);
    const emailSsoIdpsFetchedFor = useSelector(selectEmailSsoIdpsFetchedFor);
    const isSsoIdpsFetching = useSelector(selectIsSsoIdpsFetching);
    const isTwoFactorRequired = useSelector(selectIsTwoFactorRequired);
    const federatedIdentities = useSelector(selectFederatedIdentities);
    const isSSORequired = useSelector(selectIsSSORequired);
    const [loginForm, setLoginForm] = useState({
        email: '',
        password: ''
    });
    const [wasChangedFields, setWasChangedFields] = useState({
        email: false,
        password: false
    });
    const hasSsoIdentity = federatedIdentities.length > 0 && loginForm.email === emailSsoIdpsFetchedFor;
    const isSsoCheckDone = loginForm.email === emailSsoIdpsFetchedFor;
    const shouldShowSsoOnlyView = isSsoCheckDone && isSSORequired;
    const shouldShowPassword = !!loginForm.email
        && loginForm.email === emailSsoIdpsFetchedFor
        && !shouldShowSsoOnlyView;

    useErrorNotification(authError, {}, {
        position: 'top-center'
    });

    function handleSsoBack() {
        setLoginForm({email: '', password: ''});
        setWasChangedFields({email: false, password: false});
        dispatch(setFederatedIdentities([]));
        dispatch(setEmailSsoIdpsFetchedFor(''));
        dispatch(setIsSSORequired(false));
    }

    function onLoginFormChange({target}) {
        setLoginForm({
            ...loginForm,
            [target.name]: target.value
        });
        setWasChangedFields({
            ...wasChangedFields,
            [target.name]: true
        });
    }

    function handleNavigateAfterLogin() {
        const targetParam = searchParams.get('target');
        const resolvedTarget = getPostAuthRedirectUrl(targetParam);
        const redirectUrl = resolvedTarget !== DASHBOARD_PATH
            ? resolvedTarget
            : refererUrl || DASHBOARD_PATH;
        const isExchangeUrl = redirectUrl.includes(EXCHANGE_GROUP_MEMBER_PATH);

        if (isExchangeUrl || redirectUrl.includes(CEREMONY_PATH)) {
            window.location.href = redirectUrl;
        } else {
            navigate(redirectUrl);
        }
    }

    async function submit(e) {
        e.preventDefault();

        if (shouldShowPassword) {
            const authResult = await dispatch(authenticateWithTwoFactor(loginForm));
            if (authResult?.success) {
                handleNavigateAfterLogin();
            }
        } else {
            dispatch(fetchSsoIdps(loginForm.email));
        }
    }

    useEffect(() => {
        setLoginForm({
            email: '',
            password: ''
        });
    }, []);

    if (isTwoFactorRequired) {
        return (
            <div className="login-forms">
                <Notifications />
                <TwoFactorForm onSuccess={handleNavigateAfterLogin} email={loginForm.email} />
            </div>
        );
    }

    return (
        <div className="login-forms">
            <Notifications />
            <Loading isLoading={isSsoIdpsFetching} />
            {shouldShowSsoOnlyView ? (
                <>
                    <SSOIdentityView email={loginForm.email} onBack={handleSsoBack} />
                    <SsoForm wasSsoFetchedForEmail />
                </>
            ) : (
                <>
                    {!shouldShowPassword && (
                        <div className="welcome-block">
                            <h1 className="welcome-title">
                                {intl.formatMessage({id: 'page.login.welcome_title', defaultMessage: 'Welcome back'})}
                            </h1>
                            <p className="welcome-subtitle">
                                {intl.formatMessage({id: 'page.login.welcome_subtitle', defaultMessage: 'Sign in to your ValidSign account'})}
                            </p>
                        </div>
                    )}
                    <form className="credentials-form" onSubmit={submit}>
                        {shouldShowPassword ? (
                            <SSOIdentityView email={loginForm.email} onBack={handleSsoBack} />
                        ) : (
                            <EmailForm
                                email={loginForm.email}
                                wasChanged={wasChangedFields.email}
                                onLoginFormChange={onLoginFormChange}
                            />
                        )}
                        {shouldShowPassword && (
                            <PasswordForm
                                value={loginForm.password}
                                wasChanged={wasChangedFields.password}
                                onChange={onLoginFormChange}
                                classNames="login-password-form"
                                autocomplete="current-password"
                                placeholder={intl.formatMessage({id: 'esl.generic.password', defaultMessage: ''})}
                                isVisible
                            />
                        )}
                        <Buttons
                            loginForm={loginForm}
                            isLoginButtonVisible={shouldShowPassword}
                        />
                    </form>
                    {hasSsoIdentity && (
                        <>
                            <OrDivider />
                            <SsoForm wasSsoFetchedForEmail />
                        </>
                    )}
                    {!hasSsoIdentity && (
                        <>
                            <OrDivider />
                            <AppOutlinedButton
                                type="button"
                                component={Link}
                                to={ACCOUNT_REGISTRATION_PATH}
                                className="registration-button"
                                fullWidth
                                data-wdio="test-login-form-registration-button"
                            >
                                {intl.formatMessage({id: 'esl.generic.create_account', defaultMessage: 'Create a free account'})}
                            </AppOutlinedButton>
                        </>
                    )}
                    <div className="password-recovery-link-container">
                        <Link
                            to={`${PASSWORD_RECOVERY_PATH}?email=${loginForm.email}`}
                            className="password-recovery-link"
                            data-wdio="test-login-recovery-link"
                        >
                            {intl.formatMessage({id: 'esl.partials.link_forgot_password.forgot_your_password', defaultMessage: ''})}
                        </Link>
                    </div>
                </>
            )}
        </div>
    );
}
